import React from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "components/ui/button";
import { parseBookingQuery } from "modules/bookLocation/common/url/bookingQuery";
import RoomWeekView from "../components/RoomWeekView";
import { DEFAULT_TIME_ZONE } from "../weekGrid";

/**
 * The day to open on: the flow's `checkInDate`, read by the flow's own
 * validated parser, as noon on the default clock — the room's zone is not
 * known before its week loads, and noon keeps the day inside its week either
 * way. Anything else → none.
 */
export const initialDateFrom = (checkInDate: string | undefined): Date | undefined =>
  checkInDate ? moment.tz(checkInDate, "YYYY-MM-DD", DEFAULT_TIME_ZONE).hour(12).toDate() : undefined;

/**
 * `/rooms/:groupId/week` — one room's week (M3, #3036).
 *
 * The room is in the path, so a refresh or a shared link restores it. Access
 * is decided server-side: a room the visitor may not see answers 404 and the
 * view says so.
 *
 * Opened from an object-first room list (#3069), the query is the booking
 * flow's own (`bookingQuery.ts`): `checkInDate` picks the week and the day,
 * and the head count and event travel on into the slot's booking link. A
 * back control returns to the list; the history entry is that list.
 */
const RoomWeekPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId = "" } = useParams();
  const { search, state } = useLocation();
  const query = React.useMemo(() => parseBookingQuery(search), [search]);
  const initialDate = React.useMemo(() => initialDateFrom(query.checkInDate), [query.checkInDate]);
  const carry = React.useMemo(
    () => ({ numberOfGuests: query.numberOfGuests, eventType: query.eventType }),
    [query.numberOfGuests, query.eventType]
  );
  const fromRoomList = (state as { fromRoomList?: boolean } | null)?.fromRoomList === true;
  return (
    // `pb-28` clears the fixed bottom toolbar, as `PageShell` does.
    <div className="mx-auto w-full max-w-7xl px-4 pb-28 pt-4">
      {fromRoomList && (
        <Button
          type="button"
          variant="link"
          className="mb-2 h-auto p-0 text-sm font-medium"
          onClick={() => navigate(-1)}
        >
          {t("room_week.back_to_rooms")}
        </Button>
      )}
      <RoomWeekView groupId={groupId} initialDate={initialDate} carry={carry} />
    </div>
  );
};

export default RoomWeekPage;
